import { Translate } from "@redotech/redo-i18n";
import {
  AdvancedFilterData,
  FilterGroup,
  getFilterBuilder,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  ArrayStringFilterData,
  ArrayStringFilterOperator,
} from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import {
  BooleanFilterData,
  BooleanFilterOperator,
} from "@redotech/redo-views-common/advanced-filters/boolean-filter";
import {
  DateFilterData,
  DateFilterOperator,
  KnownDateFilterTimeFrame,
} from "@redotech/redo-views-common/advanced-filters/date-filter";
import {
  ExistsFilterData,
  ExistsFilterOperator,
} from "@redotech/redo-views-common/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  decodeFilterGroup,
  encodeFilterGroup,
  GenericAdvancedFilterData,
  isFilterGroup,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import {
  NumberFilterData,
  NumberFilterOperator,
  NumberValue,
} from "@redotech/redo-views-common/advanced-filters/number-filter";
import {
  StringFilterData,
  StringFilterOperator,
} from "@redotech/redo-views-common/advanced-filters/string-filter";
import { deepCopyObject } from "@redotech/util/object";
import { assertNever } from "@redotech/util/type";
import { JSXElementConstructor } from "react";
import { RedoListItem } from "../arbiter-components/list/redo-list";

export interface GenericAdvancedTableFilter<
  VALUE,
  OPERATOR extends string,
  DATA extends GenericAdvancedFilterData<VALUE, OPERATOR>,
> {
  type: DATA["type"];
  data: DATA;
  operators?: OPERATOR[];
  Icon: JSXElementConstructor<any>;
  openOnRender?: boolean;
  displayName: string;
  subFilters?: AdvancedTableFilter[];
  /** Hidden from the add-filter picker; still resolves for saved views. */
  deprecated?: boolean;
}

export interface ArrayStringTableFilter<
  T = string,
> extends GenericAdvancedTableFilter<
  string[],
  ArrayStringFilterOperator,
  ArrayStringFilterData
> {
  data: ArrayStringFilterData;
  type: AdvancedFilterType.ARRAY_TO_STRING;
  /** Noun interpolated into the search, create, and selected-count copy; pass a translated label. */
  itemLabel: string;
  itemLabelPlural?: string;
  valueFetcher?: (
    {
      filters,
      filterSearch,
      applyTableState,
    }: {
      filters: AdvancedTableFilter[];
      filterSearch?: string;
      applyTableState?: boolean;
    },
    signal: AbortSignal,
  ) => Promise<T[]>;
  itemGenerator?: (item: T) => RedoListItem<T>;
  stringify?: (item: T) => string;
  display?: (item: T) => string;
  values?: T[];
  dbSearch?: boolean;
  applyTableState?: boolean;
  useSingleSelect?: boolean;
  /** Free-text operators take several values as chips. Only set this when the backend query uses every value, not just the first. */
  multiValueInput?: boolean;
  /**
   * Text shown in the filter pill when no value is selected yet. Defaults to
   * "..." for historical parity with existing filters.
   */
  emptyValueText?: string;
  /**
   * Override the chip summary text when one or more values are selected.
   * Receives the raw selected values. If omitted, falls back to "1 <itemLabel>"
   * / "N <itemLabelPlural>" counting behaviour.
   */
  getValueSummary?: (values: T[]) => string;
  onRequestCreateItem?: () => void;
  /** Complete translated create-action text, when interpolating the item label is not grammatical. */
  createItemText?: (t: Translate) => string;
  /** Bulk-select shortcuts rendered in the dropdown (e.g. "EU" → 25 codes). */
  shortcuts?: { id: string; label: string; values: string[] }[];
  /** Opt-in: renders the property-name label as a clickable multiselect dropdown. */
  propertyNameMultiselect?: {
    options: RedoListItem<string>[];
    placeholder?: string;
  };
}

export interface DateTableFilter extends GenericAdvancedTableFilter<
  KnownDateFilterTimeFrame,
  DateFilterOperator,
  DateFilterData
> {
  type: AdvancedFilterType.DATE;
  data: DateFilterData;
  values?: KnownDateFilterTimeFrame[];
  allowFutureDates?: boolean;
  /** Defaults on. Turn off where a time of day carries no meaning. */
  allowCustomizeTimeOfDay?: boolean;
}

export interface NumberTableFilter extends GenericAdvancedTableFilter<
  NumberValue,
  NumberFilterOperator,
  NumberFilterData
> {
  type: AdvancedFilterType.NUMBER;
  data: NumberFilterData;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
  prefix?: string;
  suffix?: string;
  modifier?: number;
  secondField?: {
    scalar?: number;
    min?: number;
    max?: number;
    step?: number;
    prefix?: string;
    suffix?: string;
  };
  values?: number[];
}

/**
 * @deprecated Use ArrayStringTableFilter instead.
 * This interface is maintained only for database migration purposes.
 * All new filter implementations should use ArrayStringTableFilter.
 */
export interface StringTableFilter<
  T = string,
> extends GenericAdvancedTableFilter<
  string,
  StringFilterOperator,
  StringFilterData
> {
  type: AdvancedFilterType.STRING;
  data: StringFilterData;
  valueFetcher?: (
    { filters }: { filters: AdvancedTableFilter[] },
    signal: AbortSignal,
  ) => Promise<T[]>;
  itemGenerator?: (item: T) => RedoListItem<T>;
  stringify?: (item: T) => string; // value string
  display?: (value: T) => string; // display string
  values?: T[];
}

export interface ExistsTableFilter<
  T = boolean,
> extends GenericAdvancedTableFilter<
  boolean,
  ExistsFilterOperator,
  ExistsFilterData
> {
  type: AdvancedFilterType.EXISTS;
  data: ExistsFilterData;
  boolDisplay?: { falseDisplay: string; trueDisplay: string };
  label?: string;
}

export interface BooleanTableFilter extends GenericAdvancedTableFilter<
  boolean,
  BooleanFilterOperator,
  BooleanFilterData
> {
  type: AdvancedFilterType.BOOLEAN;
  data: BooleanFilterData;
  boolDisplay?: { falseDisplay: string; trueDisplay: string };
  label?: string;
}

export function withDefaultFilterValue(
  data: AdvancedFilterData,
): AdvancedFilterData {
  if (
    (data.type === AdvancedFilterType.BOOLEAN ||
      data.type === AdvancedFilterType.EXISTS) &&
    data.value == null
  ) {
    return { ...data, value: true };
  }
  return data;
}

export type AdvancedTableFilter =
  | ArrayStringTableFilter<any>
  | DateTableFilter
  | NumberTableFilter
  | StringTableFilter<any>
  | ExistsTableFilter
  | BooleanTableFilter;

export function encodeAdvancedFilters(filters: AdvancedTableFilter[]): string {
  let query = "";
  let first = true;
  for (const filter of filters) {
    const filterBuilder = getFilterBuilder(filter.data);
    if (!first) {
      query += ";;";
    }
    query += filter.data.name;
    query += "@@";
    query += filterBuilder.writeToString(filter.data);
    first = false;
  }
  return query;
}

export function encodeAdvancedFiltersData(
  filterData: AdvancedFilterData[],
): string {
  let query = "";
  let first = true;
  for (const filter of filterData) {
    const filterBuilder = getFilterBuilder(filter);
    if (!first) {
      query += ";;";
    }
    query += filter.name;
    query += "@@";
    query += filterBuilder.writeToString(filter);
    first = false;
  }
  return query;
}

export function decodeAdvancedFilters(
  encoded: string,
  advancedFilters: AdvancedTableFilter[],
): AdvancedTableFilter[] {
  if (!encoded) {
    return [];
  }
  const filtersStrings = encoded.split(";;");
  return filtersStrings
    .map((filterString) => {
      const [name, dataString] = filterString.split("@@");
      let filter = advancedFilters.find((f) => f.data.name === name);
      if (!filter) {
        for (const f of advancedFilters) {
          const sub = f.subFilters?.find((sf) => sf.data.name === name);
          if (sub) {
            filter = sub;
            break;
          }
        }
        if (!filter) {
          return null;
        }
      }
      const deepClone = deepCopyObject(filter);
      const filterBuilder = getFilterBuilder(deepClone.data);
      return {
        ...deepClone,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        data: filterBuilder.readFromString(name, dataString, deepClone.data),
      };
    })
    .filter((f) => f !== null) as AdvancedTableFilter[];
}

// Encode a FilterGroup to URL string format
// Format: (and:filter1@@val;;filter2@@val) or nested (and:(or:a@@1;;b@@2);;c@@3)
export function encodeAdvancedFilterGroup(group: FilterGroup): string {
  return encodeFilterGroup(group, (filter) => {
    const filterBuilder = getFilterBuilder(filter);
    return `${filter.name}@@${filterBuilder.writeToString(filter)}`;
  });
}

// Decode a URL string to FilterGroup
// Returns null if parsing fails
export function decodeAdvancedFilterGroup(
  encoded: string,
  advancedFilters: AdvancedTableFilter[],
): FilterGroup | null {
  if (!encoded) {
    return null;
  }

  return decodeFilterGroup<AdvancedFilterData>(encoded, (filterStr) => {
    const [name, dataString] = filterStr.split("@@");
    if (!name || dataString === undefined) {
      return null;
    }

    let filter = advancedFilters.find((f) => f.data.name === name);
    if (!filter) {
      for (const f of advancedFilters) {
        const sub = f.subFilters?.find((sf) => sf.data.name === name);
        if (sub) {
          filter = sub;
          break;
        }
      }
      if (!filter) {
        return null;
      }
    }

    const deepClone = deepCopyObject(filter);
    const filterBuilder = getFilterBuilder(deepClone.data);
    const data = filterBuilder.readFromString(name, dataString, deepClone.data);
    return data ?? null;
  });
}

// Convert a FilterGroup to a flat array of AdvancedTableFilter (for display purposes)
// Only works for simple AND groups at the top level
export function filterGroupToTableFilters(
  group: FilterGroup,
  advancedFilters: AdvancedTableFilter[],
): AdvancedTableFilter[] {
  const result: AdvancedTableFilter[] = [];

  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      // Recursively flatten nested groups (loses structure info)
      result.push(...filterGroupToTableFilters(clause, advancedFilters));
    } else {
      const filter = advancedFilters.find((f) => f.data.name === clause.name);
      if (filter) {
        const filterCopy = deepCopyObject(filter);
        filterCopy.data = clause;
        result.push(filterCopy);
      }
    }
  }

  return result;
}

export function dataToDummyTableFilter(
  data: AdvancedFilterData,
): AdvancedTableFilter {
  const type: AdvancedFilterType = data.type;
  switch (type) {
    case AdvancedFilterType.ARRAY_TO_STRING: {
      const tableFilter: ArrayStringTableFilter = {
        type: AdvancedFilterType.ARRAY_TO_STRING,
        Icon: () => null,
        itemLabel: "",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        data: data as ArrayStringFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.DATE: {
      const tableFilter: DateTableFilter = {
        type: AdvancedFilterType.DATE,
        Icon: () => null,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        data: data as DateFilterData,
        values: [],
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.NUMBER: {
      const tableFilter: NumberTableFilter = {
        type: AdvancedFilterType.NUMBER,
        Icon: () => null,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        data: data as NumberFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.STRING: {
      const tableFilter: StringTableFilter = {
        type: AdvancedFilterType.STRING,
        Icon: () => null,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        data: data as StringFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.EXISTS: {
      const tableFilter: ExistsTableFilter = {
        type: AdvancedFilterType.EXISTS,
        Icon: () => null,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        data: data as ExistsFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    case AdvancedFilterType.BOOLEAN: {
      const tableFilter: BooleanTableFilter = {
        type: AdvancedFilterType.BOOLEAN,
        Icon: () => null,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        data: data as BooleanFilterData,
        displayName: "",
      };
      return tableFilter;
    }
    default:
      assertNever(type);
  }
}
